import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../contexts/AuthContext';
import TcnThemeProvider from '../../../components/tcn/ThemeProvider';
import { CTAButton, Icon } from '../../../components/tcn/ui-kit';
import { TCN } from '../../../components/tcn/ui-kit/tokens';
import PriorityCard from '../../../components/tcn-modules/briefing/PriorityCard';
import {
  dubaiDayKey,
  dubaiDayKeyYesterday,
  formatPct,
  formatDiamonds,
  formatUSD,
} from '../../../components/tcn-modules/shared/format';

const formatBriefingDate = (date, locale) => {
  const d = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(d.getTime())) return '';
  const opts = { timeZone: 'Asia/Dubai' };
  const weekday = d.toLocaleDateString(locale, { ...opts, weekday: 'long' });
  const monthDay = d.toLocaleDateString(locale, { ...opts, month: 'long', day: 'numeric' });
  const dayNum = parseInt(d.toLocaleDateString('en-CA', { ...opts, day: '2-digit' }), 10);
  return { weekday, monthDay, dayNum };
};

/**
 * /tcn/me/briefing — Morning Briefing splash.
 *
 * Cached 24h per (manager, day) via localStorage flag:
 *   tcn_briefing_seen_<userId>_<YYYYMMDD>
 *
 * Visit `/tcn/me/briefing?force=1` to bypass the cache.
 *
 * 3 priority cards:
 *  1. biggest_gap_metric (from manager_current_month)
 *  2. at-risk creators count (from creator_current_month)
 *  3. biggest_bonus_gap_component (from manager_current_month) [Module 5 R2,
 *     but we already have the column populated]
 *
 * Spec: full-page splash, no tab bar.
 */
const cacheKey = (userId, day) => `tcn_briefing_seen_${userId}_${day}`;

// Maps the short `tcn_manager_daily_priority.metric_key` (used by the Pace card)
// to a localized label for the "yesterday's focus was X" recall line.
const friendlyPriorityLabel = (metricKey, t) => {
  switch (metricKey) {
    case 'diamonds':
      return t('tcn.m1.overview.diamondsTitle', 'Diamonds');
    case 'recruited':
      return t('tcn.m1.overview.recruitedTitle', 'Recruited creators');
    case 'active_days':
      return t('tcn.m1.pace.activeDays', 'Active days');
    default:
      return metricKey;
  }
};

const friendlyMetricName = (metric, t) => {
  const keys = {
    diamonds_completion_pct_vs_monthly: t('tcn.briefing.metric.diamonds', 'Monthly diamonds'),
    recruited_creators_completion_pct_vs_monthly: t('tcn.briefing.metric.recruited', 'Recruited creators'),
    valid_go_live_rate: t('tcn.briefing.metric.validGoLive', 'Valid go-LIVE rate'),
    rank_up_eligibility_rate_7d15h: t('tcn.briefing.metric.rankUpEligibility', 'Rank-up eligibility'),
    pk_penetration: t('tcn.briefing.metric.pkPenetration', 'PK penetration'),
    t14_ratio: t('tcn.briefing.metric.t14', 'T+14 success'),
    go_live_rate_15d_45h_first30: t('tcn.briefing.metric.goLive15d45h', 'Go-LIVE 15d/45h (first 30)'),
    first_30d_pk_count_20_ratio: t('tcn.briefing.metric.first30PK', 'First 30d PK ≥20'),
  };
  return keys[metric] || metric;
};

const TcnBriefing = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const force = searchParams.get('force') === '1';
  const { user, userProfile } = useAuth();

  const today = useMemo(() => dubaiDayKey(), []);
  const yesterday = useMemo(() => dubaiDayKeyYesterday(), []);
  const [manager, setManager] = useState(null);
  const [atRisk, setAtRisk] = useState({ count: 0, sample: [] });
  const [usdRate, setUsdRate] = useState(0.01);
  const [yesterdayPriority, setYesterdayPriority] = useState(null);
  const [loading, setLoading] = useState(true);

  // Skip splash if already seen today (and not force=1)
  useEffect(() => {
    if (!user?.id || force) return;
    const seen =
      typeof window !== 'undefined' &&
      window.localStorage.getItem(cacheKey(user.id, today));
    if (seen) {
      navigate('/tcn/me', { replace: true });
    }
  }, [user?.id, force, today, navigate]);

  useEffect(() => {
    if (!user?.id) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      const [
        { data: managerData },
        { data: ccm, count },
        { data: rateRow },
        { data: prioRow },
      ] = await Promise.all([
        supabase
          .from('manager_current_month')
          .select('*')
          .eq('manager_id', user.id)
          .maybeSingle(),
        supabase
          .from('creator_current_month')
          .select('creator_id, tiktok_username, display_name, alert_status', {
            count: 'exact',
          })
          .eq('manager_id', user.id)
          .neq('status', 'departed')
          .in('alert_status', ['at_risk', 'critical'])
          .order('alert_status', { ascending: false })
          .limit(3),
        supabase
          .from('metric_thresholds')
          .select('target_value')
          .eq('metric_name', 'diamond_to_usd_rate')
          .eq('server', 'MENA')
          .maybeSingle(),
        // Yesterday's "top priority" flag — surfaced as a small recall line so
        // the commitment doesn't die in isolation on the Pace card. Read-only
        // here; RLS scopes the row to the current manager.
        supabase
          .from('tcn_manager_daily_priority')
          .select('metric_key')
          .eq('manager_id', user.id)
          .eq('day_key', yesterday)
          .maybeSingle(),
      ]);
      if (cancelled) return;
      setManager(managerData || null);
      setAtRisk({ count: count ?? ccm?.length ?? 0, sample: ccm || [] });
      if (rateRow?.target_value != null) setUsdRate(Number(rateRow.target_value));
      setYesterdayPriority(prioRow?.metric_key ?? null);
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [user?.id, yesterday]);

  const handleStart = () => {
    if (user?.id && typeof window !== 'undefined') {
      window.localStorage.setItem(cacheKey(user.id, today), '1');
    }
    navigate('/tcn/me');
  };

  const userName =
    userProfile?.full_name || userProfile?.display_name || userProfile?.email?.split('@')[0] || 'there';

  return (
    <TcnThemeProvider forceTcn>
      <div
        style={{
          minHeight: '100dvh',
          // Mockup module67-support.jsx:7 — gradient cyan-tinted top → black 60%
          background: TCN.gradients.briefing,
          color: TCN.text,
          fontFamily: TCN.fontSans,
          // Mockup padding 70×24×40 (no avatar in header)
          paddingTop: 'calc(env(safe-area-inset-top, 0px) + 70px)',
          paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 40px)',
          paddingLeft: 24,
          paddingRight: 24,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <div
          style={{
            maxWidth: 560,
            margin: '0 auto',
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
            flex: 1,
          }}
        >
          {/* Eyebrow — mockup line 10-11 : 13/700/cyan/0.6 letter-spacing/uppercase */}
          <div
            style={{
              fontSize: 13,
              fontWeight: 700,
              color: TCN.cyan,
              letterSpacing: '0.6px',
              textTransform: 'uppercase',
            }}
          >
            {t('tcn.briefing.label', 'Morning briefing')}
          </div>

          {/* H1 — mockup line 12-14 : 32/700/-1px/lineHeight 1.1, 2 lignes du même H1 */}
          <h1
            style={{
              fontSize: 32,
              fontWeight: 700,
              color: TCN.text,
              letterSpacing: '-1px',
              lineHeight: 1.1,
              margin: 0,
              marginTop: 8,
            }}
          >
            {t('tcn.briefing.greeting', 'Hi {{name}} 👋', { name: userName })}
            <br />
            {t('tcn.briefing.subtitle', '3 things to know.')}
          </h1>

          {/* Date sous le H1 — mockup line 15-17 : "Tuesday · April 24 · Day 14 of month" */}
          {(() => {
            const { weekday, monthDay, dayNum } = formatBriefingDate(new Date(), t('tcn.briefing.dateLocale', 'en-AE'));
            return (
              <div
                style={{
                  fontSize: 13,
                  color: TCN.textDim,
                  marginTop: 8,
                }}
              >
                {t('tcn.briefing.dateFormat', '{{weekday}} · {{monthDay}} · Day {{dayNum}} of month', {
                  weekday,
                  monthDay,
                  dayNum,
                })}
              </div>
            );
          })()}

          {/* Recall yesterday's marked priority (PaceFlagsCard "★ Mark as
              today's top priority") so the commitment carries a small footprint
              into today's briefing instead of dying on the Pace card. Off-spec
              vs mockup A-briefing, but trades 1 subtle line for actual feedback
              loop on the marker — kept compact amber, no extra card. */}
          {yesterdayPriority && (
            <div
              style={{
                marginTop: 10,
                fontSize: 12,
                color: TCN.amber,
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                flexWrap: 'wrap',
              }}
            >
              <Icon name="flame" size={12} color={TCN.amber} />
              <span>
                {t('tcn.briefing.yesterdayPriority', 'Yesterday your focus was {{metric}} — see how it went.', {
                  metric: friendlyPriorityLabel(yesterdayPriority, t),
                })}
              </span>
            </div>
          )}

          {/* Cards container — mockup `flex: 1` flexes between H1 and CTA */}
          <div style={{ flex: 1, marginTop: 28 }}>
          {loading ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {[1, 2, 3].map((i) => (
                <div
                  key={i}
                  style={{
                    height: 88,
                    borderRadius: 14,
                    background: TCN.surface1,
                    border: `1px solid ${TCN.border}`,
                  }}
                  className="animate-pulse"
                />
              ))}
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {/* #1 Strategic focus — mockup accent amber, "You're 21% behind Diamonds pace" */}
              <PriorityCard
                rank={1}
                accent="amber"
                title={
                  manager?.biggest_gap_metric
                    ? t('tcn.briefing.p1.title', "You're {{gap}} behind {{metric}} pace", {
                        gap: formatPct(manager.biggest_gap_pct, { decimals: 0 }),
                        metric: friendlyMetricName(manager.biggest_gap_metric, t),
                      })
                    : t('tcn.briefing.p1.allGood', 'All metrics on track 💪')
                }
                body={
                  manager?.biggest_gap_metric
                    ? manager?.biggest_gap_actual != null && manager?.biggest_gap_target != null
                      ? // Mockup A-briefing card 1 sub: "4.12M of 5.20M MTD target"
                        t('tcn.briefing.p1.bodyConcrete', '{{actual}} of {{target}} MTD target', {
                          actual: formatDiamonds(manager.biggest_gap_actual),
                          target: formatDiamonds(manager.biggest_gap_target),
                        })
                      : t('tcn.briefing.p1.body', 'Translate it into actions today.')
                    : t('tcn.briefing.p1.bodyAllGood', 'No major gaps detected. Keep the momentum.')
                }
              />

              {/* #2 At-risk creators — mockup accent red */}
              <PriorityCard
                rank={2}
                accent="red"
                title={
                  atRisk.count > 0
                    ? t('tcn.briefing.p2.title', '{{count}} creators at risk', { count: atRisk.count })
                    : t('tcn.briefing.p2.titleSafe', 'No creator at risk right now')
                }
                body={
                  atRisk.count > 0
                    ? t(
                        'tcn.briefing.p2.body',
                        'Open the Creators tab to triage and add them to your 1-on-1 list.'
                      )
                    : t(
                        'tcn.briefing.p2.bodySafe',
                        "Everyone's runrate is hitting target. Use the time to focus on rank-ups."
                      )
                }
                meta={
                  atRisk.sample?.length
                    ? atRisk.sample
                        .map((c) => {
                          const h = c.tiktok_username || c.display_name || '?';
                          return h.startsWith('@') ? h : `@${h}`;
                        })
                        .join(' · ')
                    : null
                }
              />

              {/* #3 Bonus gap — mockup accent magenta */}
              <PriorityCard
                rank={3}
                accent="magenta"
                title={
                  manager?.biggest_bonus_gap_component
                    ? manager?.biggest_bonus_gap_amount_clear != null
                      ? // Mockup A-briefing card 3: "$560 recruitment bonus is unclaimed"
                        t('tcn.briefing.p3.titleConcrete', '{{amount}} {{component}} bonus is unclaimed', {
                          amount: formatUSD(Number(manager.biggest_bonus_gap_amount_clear) * usdRate),
                          component: t(
                            `tcn.briefing.bonus.${manager.biggest_bonus_gap_component}`,
                            manager.biggest_bonus_gap_component
                          ),
                        })
                      : t('tcn.briefing.p3.title', 'Bonus gap: {{component}}', {
                          component: t(
                            `tcn.briefing.bonus.${manager.biggest_bonus_gap_component}`,
                            manager.biggest_bonus_gap_component
                          ),
                        })
                    : t('tcn.briefing.p3.titleNone', 'Bonus tracking unavailable')
                }
                body={
                  manager?.biggest_bonus_gap_component
                    ? t(
                        'tcn.briefing.p3.body',
                        'Your biggest bonus opportunity is here. Close the gap before EoM.'
                      )
                    : t(
                        'tcn.briefing.p3.bodyNone',
                        "We don't have enough data yet to surface a bonus gap."
                      )
                }
              />
            </div>
          )}
          </div>{/* end cards container flex:1 */}

          {/* CTA "Start my day" — mockup module67-support.jsx:41-45 */}
          <CTAButton
            variant="primary"
            size="md"
            onClick={handleStart}
            trailing={<Icon name="arrow-right" size={16} />}
          >
            {t('tcn.briefing.cta', 'Start my day')}
          </CTAButton>
        </div>
      </div>
    </TcnThemeProvider>
  );
};

export default TcnBriefing;
